import { useCallback, useMemo, useState } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import ConditionalRender from '../../../../shared/components/ConditionalRender';
import AddOnPicker from './AddOnPicker';
import AddOnRow from './AddOnRow';
import { InfoIcon, SelectInputField } from './fields';
import { useClientFormMeta } from './clientFormMeta';
import { useAddOns } from '../../../../hooks/useAddOns';
import {
  ADD_ONS_EMPTY_COPY,
  ADD_ONS_ERROR_COPY,
  addOnsPopover,
  FIELD_LABELS,
  FIELD_PLACEHOLDERS,
  SHOW_DEMO_OPTION,
  STEP_HEADINGS,
  subscriptionLengthPopover,
  subscriptionTypePopover,
  TIER_RANK,
} from './constants';
import { clampTerm, termLabel, YEAR_TERMS } from './subscriptionTerms';
import { addOnCategory, subscriptionLabelFrom } from '../../constants';
import { useSubscriptionTypes } from '../../../../hooks/useSubscriptionTypes';
import { AddOnCategory, SpecialTerm, SubscriptionTier } from '../../../../enums/clientEnums';
import type { AddOnCatalogueItem, ClientFormValues, SubscriptionTerm } from '../../../../types/ClientManagementTypes';
import styles from './ClientFormModal.module.scss';

const SubscriptionStep = () => {
  const { isEditing, currentTier, currentExpiry } = useClientFormMeta();
  const { control, setValue, getValues } = useFormContext<ClientFormValues>();
  const { addOns: catalogue, addOnsById, isLoadingAddOns, isAddOnsLoadingError } = useAddOns();
  const { subscriptionTypes, subscriptionTypesById } = useSubscriptionTypes();

  // Only these two drive the step's own rendering; the rest of the form doesn't reach it.
  const subscriptionType = useWatch({ control, name: 'subscriptionType' });
  const subscriptionTerm = useWatch({ control, name: 'subscriptionTerm' });
  const { fields, append, remove } = useFieldArray({ control, name: 'addOns' });
  const [pickerOpen, setPickerOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState<AddOnCategory | null>(null);

  const isDemo = subscriptionType === SubscriptionTier.DEMO;

  // A tier can be upgraded but never downgraded while the subscription is live.
  const typeOptions = useMemo(() => {
    const currentRank = isEditing && currentTier ? TIER_RANK[currentTier] : -1;
    return Object.values(SubscriptionTier)
      .filter((tier) => SHOW_DEMO_OPTION || tier !== SubscriptionTier.DEMO)
      .map((tier) => ({ value: tier, label: subscriptionLabelFrom(subscriptionTypesById, tier), disabled: TIER_RANK[tier] < currentRank }));
  }, [currentTier, isEditing, subscriptionTypesById]);

  // Demo has one length; an edit leads with the client's own expiry.
  const termOptions = useMemo(() => {
    if (isDemo) return [{ value: SpecialTerm.DEMO, label: termLabel(SpecialTerm.DEMO) }];

    const current = isEditing && currentExpiry ? [{ value: SpecialTerm.CURRENT, label: termLabel(SpecialTerm.CURRENT, currentExpiry) }] : [];
    return [...current, ...YEAR_TERMS.map((term) => ({ value: term, label: termLabel(term) }))];
  }, [currentExpiry, isDemo, isEditing]);

  // New rows follow the subscription, so both handlers reconcile the list rather than an effect.
  // Saved rows are never clamped: the backend rejects a lower length, and the picker already bounds their choices.
  const applyLimitToRows = useCallback(
    (limit: SubscriptionTerm | '') => {
      getValues('addOns').forEach((row, index) => {
        if (row.heldQuantity > 0) return;
        const next = clampTerm(row.term, limit, currentExpiry);
        if (next !== row.term) setValue(`addOns.${index}.term`, next, { shouldDirty: true });
      });
    },
    [currentExpiry, getValues, setValue],
  );

  const handleTypeChange = useCallback(
    (value: string) => {
      const tier = value as SubscriptionTier;
      setValue('subscriptionType', tier, { shouldDirty: true });
      // Demo locks the length; any other change forces a fresh choice.
      const nextTerm = tier === SubscriptionTier.DEMO ? SpecialTerm.DEMO : '';
      setValue('subscriptionTerm', nextTerm, { shouldDirty: true });
      applyLimitToRows(nextTerm);
    },
    [applyLimitToRows, setValue],
  );

  const handleTermChange = useCallback(
    (value: string) => {
      const term = value as SubscriptionTerm;
      setValue('subscriptionTerm', term, { shouldDirty: true });
      applyLimitToRows(term);
    },
    [applyLimitToRows, setValue],
  );

  const handleAddAddOn = useCallback(
    (addOn: AddOnCatalogueItem) => {
      append({
        addonId: addOn.id,
        quantity: 1,
        term: addOn.hasExpiry ? subscriptionTerm || null : null,
        heldQuantity: 0,
        isExpired: false,
        heldYears: null,
        expiresAt: null,
        renew: false,
      });
    },
    [append, subscriptionTerm],
  );

  const handleRemoveAddOn = useCallback((index: number) => remove(index), [remove]);

  // Safe to clear on close: the menu unmounts and only reads the category when it next mounts.
  const handlePickerOpenChange = useCallback((nextOpen: boolean) => {
    setPickerOpen(nextOpen);
    if (!nextOpen) setActiveCategory(null);
  }, []);

  // A listed row opens the dock on its own category; the locked picker stays shut.
  const handleOpenCategory = useCallback(
    (category: AddOnCategory) => {
      if (!subscriptionTerm) return;
      setActiveCategory(category);
      setPickerOpen(true);
    },
    [subscriptionTerm],
  );

  // What the picker reads to wash out an add-on that is already listed.
  const countsById = useMemo(() => {
    const counts = new Map<number, number>();
    fields.forEach((row) => counts.set(row.addonId, (counts.get(row.addonId) ?? 0) + 1));
    return counts;
  }, [fields]);

  const addOnsMessage = () => {
    if (isAddOnsLoadingError) return ADD_ONS_ERROR_COPY;
    return isLoadingAddOns ? '' : ADD_ONS_EMPTY_COPY;
  };

  return (
    <section className={styles.step}>
      <h3 className={styles.stepHeading}>{STEP_HEADINGS.SUBSCRIPTION_INFORMATION}</h3>
      <SelectInputField
        name="subscriptionType"
        label={FIELD_LABELS.SUBSCRIPTION_TYPE}
        info={<InfoIcon content={subscriptionTypePopover(subscriptionTypes)} />}
        options={typeOptions}
        placeholder={FIELD_PLACEHOLDERS.SUBSCRIPTION_TYPE}
        onSelected={handleTypeChange}
      />
      <SelectInputField
        name="subscriptionTerm"
        label={FIELD_LABELS.SUBSCRIPTION_LENGTH}
        info={<InfoIcon content={subscriptionLengthPopover} />}
        options={termOptions}
        placeholder={FIELD_PLACEHOLDERS.SUBSCRIPTION_LENGTH}
        // Demo is fixed at two weeks, so there is nothing to choose.
        disabled={isDemo}
        onSelected={handleTermChange}
      />

      <div className={styles.addOnsHeader}>
        <span className={styles.fieldLabel}>
          {FIELD_LABELS.ADD_ONS}
          <InfoIcon content={addOnsPopover} />
        </span>
        {/* The second column titles itself only once there is a row under it. */}
        <ConditionalRender visible={fields.length > 0} hideFallback>
          <span className={`${styles.fieldLabel} ${styles.addOnLengthLabel}`}>{FIELD_LABELS.ADD_ON_LENGTH}</span>
        </ConditionalRender>
      </div>

      {/* Locked until there is a subscription length: it is what bounds every row's own. */}
      <AddOnPicker
        catalogue={catalogue}
        countsById={countsById}
        disabled={!subscriptionTerm || isLoadingAddOns || isAddOnsLoadingError}
        open={pickerOpen}
        initialCategory={activeCategory}
        onOpenChange={handlePickerOpenChange}
        onAdd={handleAddAddOn}
      />

      <div className={styles.addOnList}>
        {fields.map((row, index) => {
          const addOn = addOnsById.get(row.addonId);
          return (
            <AddOnRow
              key={row.id}
              index={index}
              name={addOn?.name ?? ''}
              category={addOn ? addOnCategory(addOn.addonType) : undefined}
              maxQuantity={addOn?.maxQuantity ?? null}
              heldQuantity={row.heldQuantity}
              isExpired={row.isExpired}
              heldYears={row.heldYears}
              expiresAt={row.expiresAt}
              hasExpiry={addOn?.hasExpiry ?? true}
              limit={subscriptionTerm}
              currentExpiry={currentExpiry}
              onRemove={handleRemoveAddOn}
              onOpenCategory={handleOpenCategory}
            />
          );
        })}
        <ConditionalRender visible={fields.length === 0} hideFallback>
          <p className={styles.addOnsMessage}>{addOnsMessage()}</p>
        </ConditionalRender>
      </div>
    </section>
  );
};

export default SubscriptionStep;
